import { Server, Terminal, Globe, ArrowRight, Boxes } from 'lucide-react';
import { useT } from '@/i18n/provider';
import { cn } from '@/lib/utils';
import { Link } from 'react-router';

const features = [
  { key: 'curl', icon: Terminal, color: 'text-primary-500', bg: 'bg-primary-50 dark:bg-primary-500/10', border: 'border-primary-200 dark:border-primary-500/20' },
  { key: 'groups', icon: Boxes, color: 'text-info-500', bg: 'bg-info-50 dark:bg-info-500/10', border: 'border-info-200 dark:border-info-500/20' },
  { key: 'eu', icon: Globe, color: 'text-success-500', bg: 'bg-success-50 dark:bg-success-500/10', border: 'border-success-200 dark:border-success-500/20' },
] as const;

const groups = ['prodEdge', 'staging', 'dbCluster'] as const;

export function FleetSection() {
  const t = useT();

  return (
    <section className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left — Text content */}
          <div className="animate-on-scroll">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/30 mb-6">
              <Server className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                {t('fleetSection.badge')}
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white text-balance">
              {t('fleetSection.title')}
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              {t('fleetSection.description')}
            </p>

            {/* Feature cards */}
            <div className="mt-8 space-y-3">
              {features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.key}
                    className="animate-on-scroll glass-subtle rounded-xl p-4 flex items-start gap-3"
                    style={{ transitionDelay: `${i * 100 + 100}ms` }}
                  >
                    <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center border shrink-0', f.bg, f.border)}>
                      <Icon className={cn('h-5 w-5', f.color)} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                        {t(`fleetSection.features.${f.key}.title`)}
                      </h3>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                        {t(`fleetSection.features.${f.key}.description`)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA link */}
            <div className="mt-8 animate-on-scroll" style={{ transitionDelay: '400ms' }}>
              <Link
                to="/agents"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors group"
              >
                {t('fleetSection.cta')}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>

          {/* Right — Visual: terminal + server groups */}
          <div className="animate-on-scroll space-y-4" style={{ transitionDelay: '200ms' }}>
            {/* Terminal mock */}
            <div className="glass-subtle rounded-2xl p-6 sm:p-7 border border-gray-200/60 dark:border-gray-800/60">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
                <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 font-mono">
                  {t('fleetSection.terminal.title')}
                </span>
              </div>
              <pre className="text-xs sm:text-sm font-mono leading-relaxed overflow-x-auto text-gray-300">
                <code>
                  <span className="text-gray-500">{t('fleetSection.terminal.comment')}</span>{'\n'}
                  <span className="text-success-400">$</span> curl -sSL{' '}
                  <span className="text-info-400">https://get.logpulse.io</span> | \{'\n'}
                  {'    '}sudo sh -s -- <span className="text-gray-400">--token</span>={' '}
                  <span className="text-success-400">ENR_…</span> \{'\n'}
                  {'             '}<span className="text-gray-400">--group</span>={' '}
                  <span className="text-success-400">prod-edge</span>{'\n'}
                  {'\n'}
                  <span className="text-gray-500">→ enrolled as AGT_b7a1f…</span>{'\n'}
                  <span className="text-gray-500">→ vector running, config in sync</span>
                </code>
              </pre>
            </div>

            {/* Server groups list */}
            <div className="glass-subtle rounded-2xl p-5 sm:p-6 border border-gray-200/60 dark:border-gray-800/60">
              <div className="flex items-center gap-2 mb-4">
                <Boxes className="h-4 w-4 text-primary-500" />
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {t('fleetSection.groups.title')}
                </span>
              </div>
              <div className="space-y-2">
                {groups.map((g) => (
                  <div
                    key={g}
                    className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-50/60 dark:bg-gray-800/40 border border-gray-200/60 dark:border-gray-700/40"
                  >
                    <span className="text-sm font-mono font-medium text-gray-900 dark:text-white">
                      {t(`fleetSection.groups.${g}`)}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t(`fleetSection.groups.${g}Meta`)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
